import {
  SubGroup,
  SubscriptionDuration,
  SubscriptionPlanDisplay,
} from "@shared/types";

export interface GetAdminSubGroupsStatsResponse {
  sub_groups: AdminSubGroupStats[];
}

export enum SubGroupPlatform {
  TELEGRAM = "Telegram",
  DISCORD = "Discord",
}

export interface AdminSubGroupStats extends SubGroup {
  num_expiring_24h: number;
  num_of_members: number;
  num_of_unauthorized: number;
  uses_groups_bot: boolean;
  platform_type: SubGroupPlatform;
}

export interface AdminSubGroup extends SubGroup {
  members: SubGroupUserMembership[];
  invitations?: Invitation[];
  configuration: SubGroupConfiguration;
}

export interface GetAdminSubGroupDataRequest {
  sub_group_id: number;
}

export interface SubGroupConfiguration {
  kick_expired_users: boolean;
  kick_unauthorized_users: boolean;
  posts: Post[];
  allow_license: boolean;
}

export interface Post {
  id: number;
  num_days: number;
  text: string;
  is_active: boolean;
}

export const LICENSE_STATUSES = ["users.licenseStatuses.expired", "users.licenseStatuses.expiresSoon", "users.licenseStatuses.active"] as const;
export type LicenseStatus = (typeof LICENSE_STATUSES)[number];

export interface SubGroupUserMembership {
  id: number;
  telegram_user_id: number;
  is_admin: boolean;
  telegram_user: TelegramUser;
  user: {
    license_status?: LicenseStatus;
    user_id?: number;
  };
  expiration_datetime?: string;
  status: string;
}

export interface TelegramUser {
  telegram_user_id: number;
  username: string;
  first_name: string;
  last_name?: string;
  is_bot: boolean;
}

export type UpdateSubGroupConfigurationRequest = Partial<
  Pick<SubGroupConfiguration, "kick_expired_users" | "kick_unauthorized_users">
> & { sub_group_id: number; posts?: Record<number, boolean> };
export type UserToPackage = Record<
  number,
  [SubscriptionPlanDisplay, SubscriptionDuration]
>;

export interface AssignLicensesRequest {
  sub_group_id: number;
  user_to_package: UserToPackage;
}

export type LicenseAvailablePackages = Record<
  SubscriptionPlanDisplay,
  Record<SubscriptionDuration, number>
>;

export interface FormattedLicenseAvailablePackage {
  subscriptionType: SubscriptionPlanDisplay;
  subscriptionDuration: SubscriptionDuration;
  numLicenses: number;
  key: number;
}

export interface AssignLicensesResponse {
  not_member?: number[];
  assigned?: number[];
  require_upgrade?: number[];
  general_error?: number[];
  missing_user?: number[];
  downgrade_error?: number[];
  dca_running_error: number[];
}

export type AssignLicensesFailureType = keyof Omit<
  AssignLicensesResponse,
  "assigned"
>;

export interface Invitation {
  id: number;
  creation_datetime: string;
  invitation_link: string;
  username: string;
  telegram_user_id: string;
  subscription_duration: string;
  accepted_datetime: string;
  num_users: number;
  status: string;
  is_new?: boolean;
  accepted_by?: TelegramUser[];
}

export type UpdateInvitationRequest = Pick<
  Invitation,
  "username" | "telegram_user_id" | "num_users" | "subscription_duration"
> & { invitation_id: number; sub_group_id: number };

export type CreateInvitationRequest = Pick<
  UpdateInvitationRequest,
  "username" | "telegram_user_id" | "sub_group_id"
>;

export type InvitationRequest = Pick<
  UpdateInvitationRequest,
  "invitation_id" | "sub_group_id"
>;

export interface FindUserMembershipRequest {
  sub_group_id: number;
  membership_id: number;
}

export type UserExpirationRequest = Pick<
  FindUserMembershipRequest,
  "sub_group_id" | "membership_id"
> & { expiration_datetime: string };
